/**
 * Add from the field — where the office sees it (approved mockup "In the
 * office": https://claude.ai/artifact/DYgYcRWa5K5pw8tBURp7fG).
 *
 *   FromTheFieldSection  on the device, location, customer and job pages:
 *                        each add with the tech's name, the date and the job it
 *                        came from. A tech note never writes over the page's
 *                        own notes box; it shows here, next to it.
 *   MoveAddDialog        pick 5: the office can move any add somewhere else.
 *   FieldAddCounts       the small photo / note counts on a Close Out card.
 *
 * Renders nothing unless the switch is on for the person looking. No bell and
 * no phone pings: a flagged add shows in the From the field chip
 * (field-chip.tsx) and nowhere else.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Camera, FileText, Flag, StickyNote } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { AttachmentImage } from "@/components/attachment-image";
import { AttachmentPreviewDialog } from "@/components/attachment-preview-dialog";
import { Dialog } from "@/components/ui/dialog";
import { InfoButton, type InfoTopic } from "@/components/info-button";
import { TeachIntro } from "@/teach/teach-card";
import {
    fieldAddJobKey,
    kindPathOf,
    moveFieldAdd,
    removeFieldAdd,
    useFieldAddSwitch,
    type FieldAddItem,
    type FieldAddJobView,
    type FieldTarget,
} from "./field-add-api";
import { InsideOnlyLine, PLACES, PlacePill, deviceName, shortDate, shortTime } from "./places";

export type FieldScopeKind = "device" | "location" | "customer" | "job";

const SCOPE_PATH: Record<Exclude<FieldScopeKind, "job">, string> = {
    device: "devices",
    location: "locations",
    customer: "customers",
};

export const FROM_THE_FIELD_HELP: InfoTopic = {
    title: "From the field",
    lines: [
        "These are photos, notes and files a tech added from the job with the green +. Each one shows who added it, when, and the job it came from.",
        "The colored tag says where the tech put it: this job, a device, the location or the customer. Put somewhere wrong? Tap Move.",
        "A tech's note never writes over the notes box on this page. It stays here, next to it.",
        "None of this goes to the customer. Tech photos are kept out of the Close Out packet and can't be checked to send.",
    ],
    shots: [
        {
            src: "/help/field-add/office-section.png",
            alt: "The From the field section on an office page",
            marks: [
                { x: 14, y: 6, label: "How many the techs added here", side: "right" },
                { x: 26, y: 38, label: "Who added it, and when", side: "right" },
                { x: 92, y: 30, label: "Move it somewhere else, or remove it", side: "left" },
                { x: 50, y: 93, label: "Never goes to the customer", side: "above" },
            ],
        },
    ],
};

async function fetchScope(kind: FieldScopeKind, id: string): Promise<{ on: boolean; items: FieldAddItem[] }> {
    if (kind === "job") {
        const v = await api.get<FieldAddJobView>(`/api/field-adds/jobs/${id}`);
        return { on: v.on === true, items: v.thisJob ?? [] };
    }
    return api.get<{ on: boolean; items: FieldAddItem[] }>(`/api/field-adds/${SCOPE_PATH[kind]}/${id}`);
}

/** Its own key: the job view (`fieldAddJobKey`) holds a different shape. */
export const fieldScopeKey = (kind: FieldScopeKind, id: string) => ["field-adds", "list", kind, id] as const;

export function FromTheFieldSection({ kind, id, className }: { kind: FieldScopeKind; id: string; className?: string }) {
    const sw = useFieldAddSwitch();
    const on = sw.data?.on === true;
    const q = useQuery({
        queryKey: fieldScopeKey(kind, id),
        queryFn: () => fetchScope(kind, id),
        enabled: on && !!id,
        staleTime: 30_000,
    });
    const [moving, setMoving] = useState<FieldAddItem | null>(null);
    const [preview, setPreview] = useState<FieldAddItem | null>(null);
    const items = q.data?.on ? q.data.items : [];
    if (!on || items.length === 0) return null;
    const tone = kind === "job" ? PLACES.job : PLACES[kind];
    return (
        <section
            id="from-the-field"
            className={cn("scroll-mt-24 rounded-xl border bg-card", className)}
            data-testid={`from-the-field-${kind}`}
        >
            <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2.5">
                <div className="flex items-center gap-2">
                    <span
                        className={cn(
                            "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[13px] font-bold",
                            tone.pill,
                        )}
                    >
                        <tone.Icon className="h-4 w-4" aria-hidden />
                        From the field · {items.length}
                    </span>
                    <InfoButton topic={FROM_THE_FIELD_HELP} />
                </div>
                {kind === "job" && (
                    <span className="text-xs text-foreground/70">
                        Everything added during this job, wherever the tech sent it
                    </span>
                )}
            </div>
            <div className="divide-y">
                {items.map((item) => (
                    <FieldRow
                        key={item.id}
                        item={item}
                        showPlace={kind === "job"}
                        showJob={kind !== "job"}
                        canManage={sw.data?.isOffice === true}
                        onMove={() => setMoving(item)}
                        onOpen={() => setPreview(item)}
                    />
                ))}
            </div>
            <div className="px-4 py-2.5">
                <InsideOnlyLine>Inside only. Tech adds never go to the customer.</InsideOnlyLine>
            </div>
            {sw.data?.isOffice && <TeachIntro featureKey="field-add.office" when />}
            {moving && <MoveAddDialog item={moving} onClose={() => setMoving(null)} />}
            <AttachmentPreviewDialog
                open={preview != null}
                onClose={() => setPreview(null)}
                attachmentId={preview?.id ?? null}
                fileName={preview?.fileName ?? undefined}
            />
        </section>
    );
}

function FieldRow({
    item,
    showPlace,
    showJob,
    canManage,
    onMove,
    onOpen,
}: {
    item: FieldAddItem;
    showPlace: boolean;
    showJob: boolean;
    canManage: boolean;
    onMove: () => void;
    onOpen: () => void;
}) {
    const qc = useQueryClient();
    const [armed, setArmed] = useState(false);
    const refresh = () => void qc.invalidateQueries({ queryKey: ["field-adds"] });
    const remove = useMutation({
        mutationFn: () => removeFieldAdd(kindPathOf(item), item.id),
        onSuccess: () => {
            toast.success("Removed");
            refresh();
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Couldn't remove it."),
    });
    const read = useMutation({
        mutationFn: () => api.post(`/api/field-adds/${kindPathOf(item)}/${item.id}/read`, {}),
        onSuccess: refresh,
        onError: (e: any) => toast.error(e?.body?.message ?? "Couldn't mark it read."),
    });
    const by = [item.authorName ?? "A tech", `${shortDate(item.createdAt)} · ${shortTime(item.createdAt)}`];
    return (
        <div className="flex items-start gap-3 px-4 py-3" data-testid="from-the-field-row">
            {item.kind === "photo" ? (
                <button type="button" onClick={onOpen} className="shrink-0" aria-label="Open the photo">
                    <AttachmentImage
                        attachmentId={item.id}
                        alt="Photo from the field"
                        className="h-12 w-14 rounded-md border object-cover"
                    />
                </button>
            ) : (
                <span
                    className={cn(
                        "grid h-12 w-14 shrink-0 place-items-center rounded-md border",
                        PLACES[item.target].pill,
                    )}
                >
                    {item.kind === "note" ? <StickyNote className="h-5 w-5" /> : <FileText className="h-5 w-5" />}
                </span>
            )}
            <div className="min-w-0 flex-1 space-y-1">
                {showPlace && <PlacePill item={item} />}
                {item.kind === "note" ? (
                    <p className="whitespace-pre-wrap text-sm leading-snug">“{item.body}”</p>
                ) : item.kind === "file" ? (
                    <button type="button" onClick={onOpen} className="text-left text-sm font-medium underline">
                        {item.fileName ?? "File"}
                    </button>
                ) : item.body ? (
                    <p className="text-sm">{item.body}</p>
                ) : null}
                <p className="text-xs text-foreground/70">
                    {by.join(" · ")}
                    {showJob && item.jobId && (
                        <>
                            {" · "}
                            <Link to={`/jobs/${item.jobId}`} className="text-primary hover:underline">
                                Job #{item.jobNumber ?? "…"}
                            </Link>
                        </>
                    )}
                    {item.target === "device" && !showPlace && item.deviceLabel ? ` · ${deviceName(item)}` : ""}
                </p>
                {item.needsOffice && (
                    <span
                        className={cn(
                            "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold",
                            item.readAt
                                ? "bg-muted text-foreground/70"
                                : "bg-red-50 text-red-800 dark:bg-red-500/15 dark:text-red-200",
                        )}
                    >
                        <Flag className="h-3 w-3" aria-hidden />
                        {item.readAt
                            ? `Office needs to see this · read by ${item.readByName?.split(" ")[0] ?? "the office"}`
                            : "Office needs to see this"}
                    </span>
                )}
            </div>
            {canManage && (
                <div className="flex shrink-0 flex-col items-end gap-1.5">
                    {item.needsOffice && !item.readAt && (
                        <button
                            type="button"
                            disabled={read.isPending}
                            onClick={() => read.mutate()}
                            className="h-8 rounded-md border px-2.5 text-xs font-semibold"
                        >
                            Mark read
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={onMove}
                        data-testid="from-the-field-move"
                        className="h-8 rounded-md border px-2.5 text-xs font-semibold"
                    >
                        Move
                    </button>
                    <button
                        type="button"
                        disabled={remove.isPending}
                        onClick={() => (armed ? remove.mutate() : setArmed(true))}
                        className={cn(
                            "h-8 rounded-md border px-2.5 text-xs font-semibold",
                            armed ? "border-red-700 bg-red-700 text-white" : "text-red-700",
                        )}
                    >
                        {armed ? "Click again to remove" : "Remove"}
                    </button>
                </div>
            )}
        </div>
    );
}

/** Pick 5: re-point an add. Devices come from the job it was added on. */
export function MoveAddDialog({ item, onClose }: { item: FieldAddItem; onClose: () => void }) {
    const qc = useQueryClient();
    const jobQ = useQuery({
        queryKey: fieldAddJobKey(item.jobId ?? ""),
        queryFn: () => api.get<FieldAddJobView>(`/api/field-adds/jobs/${item.jobId}`),
        enabled: !!item.jobId,
    });
    const [target, setTarget] = useState<FieldTarget>(item.target);
    const [assetId, setAssetId] = useState<string | null>(item.assetId);
    const move = useMutation({
        mutationFn: () => moveFieldAdd(kindPathOf(item), item.id, target, target === "device" ? assetId : null),
        onSuccess: () => {
            toast.success("Moved");
            void qc.invalidateQueries({ queryKey: ["field-adds"] });
            onClose();
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Couldn't move it."),
    });
    const devices = jobQ.data?.devices ?? [];
    const targets: FieldTarget[] = ["job", "device", "location", "customer"];
    return (
        <Dialog open onClose={onClose} title="Move this add" className="max-w-lg">
            <div className="space-y-3">
                <p className="text-sm text-foreground/80">Pick where it belongs. The tech's words don't change.</p>
                <div className="grid grid-cols-2 gap-2">
                    {targets.map((t) => {
                        const p = PLACES[t];
                        const picked = target === t;
                        return (
                            <button
                                key={t}
                                type="button"
                                aria-pressed={picked}
                                data-testid={`move-to-${t}`}
                                onClick={() => setTarget(t)}
                                className={cn(
                                    "flex items-center gap-2 rounded-lg border-2 px-3 py-2.5 text-left",
                                    p.ink,
                                    picked ? p.on : "border-border",
                                )}
                            >
                                <p.Icon className="h-5 w-5 shrink-0" aria-hidden />
                                <span>
                                    <b className="block text-sm text-foreground">{p.label}</b>
                                    <span className="block text-xs text-foreground/70">
                                        {t === "device" ? "Pick which one below" : p.sub}
                                    </span>
                                </span>
                            </button>
                        );
                    })}
                </div>
                {target === "device" && (
                    <div className="space-y-1.5">
                        {devices.length === 0 && (
                            <p className="text-sm text-foreground/70">That job has no devices on it.</p>
                        )}
                        {devices.map((d) => (
                            <button
                                key={d.assetId}
                                type="button"
                                aria-pressed={assetId === d.assetId}
                                onClick={() => setAssetId(d.assetId)}
                                className={cn(
                                    "flex w-full items-center justify-between rounded-lg border-2 px-3 py-2 text-left text-sm",
                                    assetId === d.assetId ? PLACES.device.on : "border-border",
                                )}
                            >
                                <span>
                                    <b>{d.label}</b>
                                    <span className="block text-xs text-foreground/70">
                                        {[d.serial ? `SN ${d.serial}` : null, d.location].filter(Boolean).join(" · ")}
                                    </span>
                                </span>
                            </button>
                        ))}
                    </div>
                )}
                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="h-10 rounded-lg border px-4 text-sm font-semibold"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        data-testid="move-save"
                        disabled={move.isPending || (target === "device" && !assetId)}
                        onClick={() => move.mutate()}
                        className="h-10 rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground disabled:opacity-50"
                    >
                        Move it
                    </button>
                </div>
            </div>
        </Dialog>
    );
}

/** The small counts on a Close Out card; a link to the job page's list. */
export function FieldAddCounts({
    jobId,
    counts,
}: {
    jobId: string;
    counts: { photos: number; notes: number } | null | undefined;
}) {
    const sw = useFieldAddSwitch();
    if (sw.data?.on !== true || !counts || counts.photos + counts.notes === 0) return null;
    return (
        <Link
            to={`/jobs/${jobId}#from-the-field`}
            onClick={(e) => e.stopPropagation()}
            title="Added by the tech from the field. Inside only: never in the customer packet."
            data-testid="closeout-field-counts"
            className="inline-flex items-center gap-1.5"
        >
            {counts.photos > 0 && (
                <span
                    className={cn(
                        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold",
                        PLACES.job.pill,
                    )}
                >
                    <Camera className="h-3 w-3" aria-hidden /> {counts.photos}
                </span>
            )}
            {counts.notes > 0 && (
                <span
                    className={cn(
                        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold",
                        PLACES.job.pill,
                    )}
                >
                    <StickyNote className="h-3 w-3" aria-hidden /> {counts.notes}
                </span>
            )}
        </Link>
    );
}
